import { type ReactNode, useEffect, useReducer, useRef, useState } from 'react';

/**
 * One unit of the agent's *work* within a turn: a reasoning step or a
 * non-render tool call. `ActivityFeed` collapses a run of these into a single
 * live indicator instead of one row per call (a data pull can fire dozens of
 * `Bash`/query calls — see the wall-of-checkmarks this replaces).
 */
export type ActivityStep =
  | { key: string; kind: 'reasoning'; text: string; active: boolean }
  | { key: string; kind: 'tool'; label: string; detail?: string; state: 'active' | 'done' | 'error' };

function stepDone(s: ActivityStep): boolean {
  return s.kind === 'tool' ? s.state !== 'active' : !s.active;
}

/**
 * Compact one-liner for the running header: a reasoning step's *current*
 * sentence (last non-empty line), or a tool's detail/label. Keeps a long
 * thought chain from spilling the whole transcript into the live indicator.
 */
function stepLine(s: ActivityStep): string {
  if (s.kind === 'reasoning') {
    const line = s.text.trim().split('\n').filter(Boolean).pop();
    return line || 'Thinking…';
  }
  return s.detail || s.label;
}

/**
 * Full text for a settled/expanded row: the whole reasoning thought (rendered
 * multi-line), or a tool's detail/label. The collapsed view stays compact; the
 * detail the reader opts into keeps the complete thought the old inline row had.
 */
function stepText(s: ActivityStep): string {
  if (s.kind === 'reasoning') return s.text.trim() || 'Thinking…';
  return s.detail || s.label;
}

function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 1) return '<1s';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

/** Re-renders ~2×/s while `active`, so a live elapsed timer keeps counting. */
function useNowTick(active: boolean): number {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [active]);
  return performance.now();
}

const DIM = { color: 'var(--fg-3)', fontSize: 13.5, lineHeight: 1.5 } as const;

/**
 * Gap between a row's leading glyph (pulse or ✓) and its text. Shared with the
 * loading skeleton's "Analyzing…" row (`Thinking`) so the pulse-to-text spacing
 * is identical wherever a pulse appears.
 */
export const GLYPH_GAP = 10;

/** Live pulse (three bouncing dots) used while a step is in flight. */
function Pulse() {
  return (
    <span className="he-dots" aria-hidden>
      <i />
      <i />
      <i />
    </span>
  );
}

function StatusGlyph({ state }: { state: 'done' | 'error' }) {
  return (
    <span aria-hidden style={{ color: state === 'error' ? 'var(--he-danger)' : 'var(--fg-3)' }}>
      {state === 'error' ? '⚠' : '✓'}
    </span>
  );
}

function glyphState(s: ActivityStep): 'done' | 'error' {
  return s.kind === 'tool' && s.state === 'error' ? 'error' : 'done';
}

/**
 * One dim indicator line: a leading glyph, a headline, and an optional trailing
 * cell (a duration, or the running timer). Shared by the running header, a
 * single settled step, and each row of the expanded list.
 */
function StepRow({
  glyph,
  headline,
  italic,
  multiline,
  trailing,
  glyphWidth = 16,
  align = 'start',
}: {
  glyph: ReactNode;
  headline: string;
  italic?: boolean;
  multiline?: boolean;
  trailing?: ReactNode;
  // `null` lets the glyph size to its content — the pulse is ~3× the width of a
  // single ✓ char, so a fixed cell would clip it into the text.
  glyphWidth?: number | null;
  // 'center' vertically centers a single-line row (the running pulse, so the
  // dots sit on the text baseline); 'start' aligns the glyph to the first line
  // for settled rows whose reasoning text may wrap.
  align?: 'start' | 'center';
}) {
  const center = align === 'center';
  return (
    <div style={{ display: 'flex', alignItems: center ? 'center' : 'flex-start', gap: GLYPH_GAP, ...DIM }}>
      <span
        style={{
          flexShrink: 0,
          marginTop: center ? 0 : 3,
          ...(glyphWidth != null ? { width: glyphWidth } : null),
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {glyph}
      </span>
      <span style={{ minWidth: 0, fontStyle: italic ? 'italic' : 'normal', whiteSpace: multiline ? 'pre-wrap' : 'normal' }}>
        {headline}
      </span>
      {trailing != null && (
        <span style={{ flexShrink: 0, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{trailing}</span>
      )}
    </div>
  );
}

/**
 * The agent's work for one stretch of a turn, as a single dim indicator.
 *
 * While running: a pulse + the current step's headline + a live elapsed timer,
 * so you see *what* it's doing and *how long* without a row per call. Once the
 * run settles it collapses to `✓ Worked for Ns` — expandable to the full step
 * list, each with its own duration. `live` marks the turn as still streaming so
 * a run whose last tool has settled but whose turn hasn't still shows as active.
 */
export function ActivityFeed({ steps, live }: { steps: ActivityStep[]; live?: boolean }) {
  const running = live || steps.some((s) => !stepDone(s));
  const now = useNowTick(running);
  // groupParts never emits an empty run, but degrade rather than render an empty
  // "0 steps" shell if a caller ever passes one (the never-throw invariant).
  const empty = steps.length === 0;

  // Per-step timing, tracked client-side (parts carry no timestamps): record a
  // start the first render a step appears, an end the first render it settles.
  // A step first seen already settled is `measured: false` — it arrived
  // pre-settled (a restored thread replays finished parts), so any elapsed we'd
  // compute is fiction; we omit its duration rather than show a false `<1s`.
  const times = useRef(new Map<string, { start: number; end?: number; measured: boolean }>());
  useEffect(() => {
    const t = performance.now();
    for (const s of steps) {
      let rec = times.current.get(s.key);
      if (!rec) {
        rec = { start: t, measured: !stepDone(s) };
        times.current.set(s.key, rec);
      }
      if (stepDone(s) && rec.end == null) rec.end = t;
    }
  }, [steps]);

  const [open, setOpen] = useState(false);

  // Total spans the measured steps only; null when nothing was measured.
  let earliest = Infinity;
  let latest = 0;
  for (const rec of times.current.values()) {
    if (!rec.measured) continue;
    earliest = Math.min(earliest, rec.start);
    latest = Math.max(latest, rec.end ?? now);
  }
  const totalMs = Number.isFinite(earliest) ? latest - earliest : null;

  const doneCount = steps.filter(stepDone).length;
  // A collapsed run shows one glyph for the whole run, so it has to answer
  // "did anything fail" — a ✓ over a run containing a failure is the summary
  // asserting the opposite of what expanding it shows. The count rides along
  // because the glyph alone cannot say how much.
  const failedCount = steps.filter((s) => glyphState(s) === 'error').length;

  if (empty) return null;

  if (running) {
    const current = steps[steps.length - 1];
    return (
      <StepRow
        glyph={<Pulse />}
        glyphWidth={null}
        align="center"
        headline={current ? stepLine(current) : 'Working…'}
        italic={current?.kind === 'reasoning'}
        trailing={
          <>
            {doneCount > 0 && `${doneCount} step${doneCount > 1 ? 's' : ''} · `}
            {fmtDur(totalMs ?? 0)}
          </>
        }
      />
    );
  }

  // Settled. One step reads fine as a plain line; more than one collapses behind
  // a summary the reader can expand for the per-step breakdown.
  if (steps.length === 1) {
    const s = steps[0];
    const dur = timeOf(times.current, s.key, now);
    return (
      <StepRow
        glyph={<StatusGlyph state={glyphState(s)} />}
        headline={stepText(s)}
        italic={s.kind === 'reasoning'}
        multiline={s.kind === 'reasoning'}
        trailing={dur != null ? fmtDur(dur) : undefined}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button
        type="button"
        className="he-focusable"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          ...DIM,
        }}
      >
        <span style={{ flexShrink: 0, width: 16, display: 'inline-flex', justifyContent: 'center' }}>
          <StatusGlyph state={failedCount > 0 ? 'error' : 'done'} />
        </span>
        {totalMs != null ? (
          <>
            <span>Worked for {fmtDur(totalMs)}</span>
            <span style={{ color: 'var(--fg-3)' }}>· {steps.length} steps</span>
          </>
        ) : (
          <span>{steps.length} steps</span>
        )}
        {failedCount > 0 && <span style={{ color: 'var(--he-danger)' }}>· {failedCount} failed</span>}
        <span aria-hidden style={{ transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }}>
          ›
        </span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 24 }}>
          {steps.map((s) => {
            const dur = timeOf(times.current, s.key, now);
            return (
              <StepRow
                key={s.key}
                glyph={<StatusGlyph state={glyphState(s)} />}
                glyphWidth={12}
                headline={stepText(s)}
                italic={s.kind === 'reasoning'}
                multiline={s.kind === 'reasoning'}
                trailing={dur != null ? fmtDur(dur) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function timeOf(
  map: Map<string, { start: number; end?: number; measured: boolean }>,
  key: string,
  now: number,
): number | null {
  const rec = map.get(key);
  if (!rec || !rec.measured) return null;
  return (rec.end ?? now) - rec.start;
}
